import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { governanceOverlaysService } from '../services/governanceOverlaysService';
import AddPropertyModal from '../components/AddPropertyModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { ArrowLeft, Plus, Trash2, Save, X, Edit, Shield } from 'lucide-react';

interface Entry { key: string; value: any }

const CreateGovernanceOverlay: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, setUserAndToken } = useUser();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  const [entries, setEntries] = useState<Entry[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<any>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const convertToApiKey = (displayName: string): string => {
    return displayName.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, '_').trim();
  };

  const startEditing = (key: string, value: any) => {
    setEditingKey(key);
    if (Array.isArray(value)) setEditValue([...(value as any[])]);
    else if (typeof value === 'object' && value !== null) setEditValue({ ...(value as any) });
    else setEditValue(value ?? '');
  };
  const cancelEditing = () => { setEditingKey(null); setEditValue(null); };

  const updateFormValue = (_key: string, value: any) => setEditValue(value);

  const updateObjectValue = (_parent: string, childKey: string, value: string) => {
    setEditValue((prev: any) => ({ ...(prev || {}), [childKey]: value }));
  };
  const removeObjectKey = (_parent: string, childKey: string) => {
    setEditValue((prev: any) => { const copy = { ...(prev || {}) }; delete (copy as any)[childKey]; return copy; });
  };
  const addObjectKey = () => setEditValue((prev: any) => ({ '': '', ...(prev || {}) }));

  const updateArrayValue = (_parent: string, index: number, value: string) => {
    setEditValue((prev: any[]) => { const arr = Array.isArray(prev) ? [...prev] : []; arr[index] = value; return arr; });
  };
  const addArrayItem = () => setEditValue((prev: any[]) => (Array.isArray(prev) ? ['', ...prev] : ['']));
  const removeArrayItem = (_parent: string, index: number) => {
    setEditValue((prev: any[]) => { const arr = Array.isArray(prev) ? [...prev] : []; return arr.filter((_, i) => i !== index); });
  };

  const updatePropertyName = (oldKey: string, newKey: string) => {
    const apiKey = convertToApiKey(newKey);
    setEntries(prev => prev.map(e => e.key === oldKey ? ({ ...e, key: apiKey }) : e));
    if (editingKey === oldKey) setEditingKey(apiKey);
  };
  const updatePropertyType = (key: string, newType: 'value' | 'object' | 'array') => {
    let defaultValue: any = '';
    if (newType === 'object') defaultValue = { '': '' };
    if (newType === 'array') defaultValue = [''];
    setEntries(prev => prev.map(e => e.key === key ? ({ ...e, value: defaultValue }) : e));
    if (editingKey === key) setEditValue(defaultValue);
  };

  const handleAddProperty = (property: Entry) => setEntries(prev => [property, ...prev]);
  const removeEntry = (key: string) => { setDeleteTarget(key); setShowDeleteModal(true); };
  const confirmDelete = () => { if (deleteTarget) setEntries(prev => prev.filter(e => e.key !== deleteTarget)); setDeleteTarget(null); setShowDeleteModal(false); };
  const cancelDelete = () => { setDeleteTarget(null); setShowDeleteModal(false); };

  const saveEntry = () => {
    if (!editingKey) return;
    setEntries(prev => prev.map(e => e.key === editingKey ? ({ ...e, value: editValue }) : e));
    setEditingKey(null); setEditValue(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Overlay name is required'); return; }
    // validate empty keys
    for (const entry of entries) {
      if (!entry.key || entry.key.trim() === '') { setError('Description key cannot be empty.'); return; }
      if (typeof entry.value === 'object' && entry.value !== null && !Array.isArray(entry.value)) {
        const empty = Object.keys(entry.value).filter(k => k.trim() === '');
        if (empty.length) { setError('Object property keys cannot be empty.'); return; }
      }
    }
    setLoading(true); setError(null);
    try {
      const body: any = { name: name.trim(), description: description.trim(), is_default: isDefault };
      if (entries.length) body.entries = entries.map(e => ({ key: e.key, value: e.value }));
      await governanceOverlaysService.createGovernanceOverlay(accessToken, setUserAndToken, body);
      navigate('/governance-overlays');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create governance overlay');
    } finally { setLoading(false); }
  };

  const renderValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      return (
        <div className="space-y-2">
          <ul className="space-y-1">
            {value.map((item: string, index: number) => (
              <li key={index} className="flex items-start">
                <span className="text-gray-500 text-sm mr-2">•</span>
                <span className="text-sm text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div className="space-y-2">
          <div className="overflow-hidden">
            <table className="min-w-full">
              <tbody>
                {Object.entries(value).map(([subKey, subValue]) => (
                  <tr key={subKey} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-2 pr-4 text-sm text-gray-600 font-medium align-top w-1/3">
                      {subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                    </td>
                    <td className="py-2 text-sm text-gray-700 align-top">{subValue as string}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    return <div className="text-sm text-gray-700">{value}</div>;
  };

  const renderEditField = (key: string, value: any) => {
    const currentValue = key === editingKey ? editValue : value;
    const isNew = key.startsWith('property_');
    if (isNew) {
      const currentType = Array.isArray(currentValue) ? 'array' : typeof currentValue === 'object' && currentValue !== null ? 'object' : 'value';
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-slate-600 mb-1">Description Key</label>
              <input type="text" value={key.replace(/^property_/, '')} onChange={(e) => updatePropertyName(key, e.target.value)} className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Enter key" />
            </div>
            <div className="w-32">
              <label className="block text-xs text-slate-600 mb-1">Value Type</label>
              <select value={currentType} onChange={(e) => updatePropertyType(key, e.target.value as any)} className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100">
                <option value="value">Value</option>
                <option value="object">Object</option>
                <option value="array">Array</option>
              </select>
            </div>
          </div>
          {currentType === 'value' && (
            <div>
              <label className="block text-xs text-slate-600 mb-1">Value</label>
              <input type="text" value={currentValue} onChange={(e) => updateFormValue(key, e.target.value)} className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Enter value" />
            </div>
          )}
          {currentType === 'object' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs text-slate-600">Object Properties</label>
                <button type="button" onClick={addObjectKey} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"><Plus className="w-3 h-3"/> Add</button>
              </div>
              {Object.entries(currentValue).map(([subKey, subValue], index) => (
                <div key={index} className="flex items-center gap-2">
                  <input type="text" value={subKey} onChange={(e) => { const newObj:any = { ...currentValue }; delete newObj[subKey]; newObj[convertToApiKey(e.target.value)] = subValue; updateFormValue(key, newObj); }} className="w-1/3 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Key" />
                  <input type="text" value={subValue as string} onChange={(e) => updateObjectValue(key, subKey, e.target.value)} className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Value" />
                  <button type="button" onClick={() => removeObjectKey(key, subKey)} className="text-red-500 hover:text-red-700 p-1" title="Remove"><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
            </div>
          )}
          {currentType === 'array' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between"><label className="block text-xs text-slate-600">Array Items</label><button type="button" onClick={addArrayItem} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"><Plus className="w-3 h-3"/> Add Item</button></div>
              {currentValue.map((item: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <input type="text" value={item} onChange={(e) => updateArrayValue(key, index, e.target.value)} className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Array item" />
                  <button type="button" onClick={() => removeArrayItem(key, index)} className="text-red-500 hover:text-red-700 p-1" title="Remove"><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    return renderValueContent(key, currentValue);
  };

  const renderValueContent = (key: string, currentValue: any) => {
    if (Array.isArray(currentValue)) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Array Items</span><button type="button" onClick={addArrayItem} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"><Plus className="w-3 h-3"/> Add Item</button></div>
          {currentValue.map((item: string, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <input type="text" value={item} onChange={(e) => updateArrayValue(key, index, e.target.value)} className="flex-1 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Array item" />
              <button type="button" onClick={() => removeArrayItem(key, index)} className="text-red-500 hover:text-red-700 p-1" title="Remove"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      );
    }
    if (typeof currentValue === 'object' && currentValue !== null) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Object Properties</span><button type="button" onClick={addObjectKey} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"><Plus className="w-3 h-3"/> Add</button></div>
          {Object.entries(currentValue).map(([subKey, subValue], index) => (
            <div key={index} className="flex items-center gap-2">
              <input type="text" value={subKey} onChange={(e) => { const newObj:any = { ...currentValue }; delete newObj[subKey]; newObj[convertToApiKey(e.target.value)] = subValue; updateFormValue(key, newObj); }} className="w-1/3 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Key" />
              <input type="text" value={subValue as string} onChange={(e) => updateObjectValue(key, subKey, e.target.value)} className="flex-1 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Value" />
              <button type="button" onClick={() => removeObjectKey(key, subKey)} className="text-red-500 hover:text-red-700 p-1" title="Remove"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      );
    }
    return (<input type="text" value={currentValue ?? ''} onChange={(e) => updateFormValue(key, e.target.value)} className="w-full border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Enter value" />);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/governance-overlays')} className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition"><ArrowLeft className="w-5 h-5"/></button>
          <div className="flex items-center gap-2">
            <Shield className="w-7 h-7 text-slate-600" />
            <h1 className="text-2xl font-bold text-gray-900">Create Governance Overlay</h1>
          </div>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Main Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="e.g., Payment Terms Agreement" required />
            </div>
            <div className="flex flex-col justify-end">
              <div className="flex items-center">
                <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500" />
                <span className="ml-2 text-sm text-slate-700">Set as default overlay</span>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Describe this overlay..." />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Descriptions</h2>
            <button type="button" onClick={() => setShowAddModal(true)} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"><Plus className="w-4 h-4"/> Add Description</button>
          </div>
          <div className="p-6 space-y-6">
            {entries.map((entry) => {
              const isEditing = editingKey === entry.key;
              return (
                <div key={entry.key} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-slate-900">{entry.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                    <div className="flex items-center space-x-2">
                      {!isEditing ? (
                        <>
                          <button type="button" onClick={() => startEditing(entry.key, entry.value)} className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors" title="Edit"><Edit className="w-4 h-4"/></button>
                          <button type="button" onClick={() => removeEntry(entry.key)} className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors" title="Remove"><Trash2 className="w-4 h-4"/></button>
                        </>
                      ) : (
                        <>
                          <button type="button" onClick={saveEntry} className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors" title="Save"><Save className="w-4 h-4"/></button>
                          <button type="button" onClick={cancelEditing} className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors" title="Cancel"><X className="w-4 h-4"/></button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-3">{isEditing ? renderEditField(entry.key, entry.value) : renderValue(entry.key, entry.value)}</div>
                </div>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4"><div className="flex items-center"><X className="w-5 h-5 text-red-500 mr-2"/><span className="text-red-700">{error}</span></div></div>
        )}

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/governance-overlays')} className="px-6 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition" disabled={loading}>Cancel</button>
          <button type="submit" className="px-6 py-2 rounded-lg bg-[#4a67b7] text-white font-semibold shadow hover:bg-primary/90 transition flex items-center gap-2" disabled={loading}><Save className="w-4 h-4"/>{loading ? 'Creating...' : 'Create Overlay'}</button>
        </div>
      </form>

      <AddPropertyModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add New Description" submitLabel="Add Description" onSave={(prop) => { handleAddProperty({ key: prop.key, value: prop.value }); setShowAddModal(false); }} />

      <ConfirmDeleteModal isOpen={showDeleteModal} onClose={cancelDelete} onConfirm={confirmDelete} title="Delete Description" message="Are you sure you want to delete this description? This action cannot be undone." itemName={deleteTarget ? deleteTarget.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : undefined} isLoading={false} />
    </div>
  );
};

export default CreateGovernanceOverlay;
